import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PageHead } from "@/pages/app/AppShell";
import { EventCard } from "@/components/EventCard";
import EventDialog from "@/components/EventDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const PRESETS = ["/img/summit.jpg", "/img/gala.jpg", "/img/roundtable.jpg", "/img/hero.jpg"];
const NUMS = [
  ["partnership_spend", "Partnership spend ($)"], ["revenue_generated", "Revenue generated ($)"],
  ["deals_won", "Deals won"], ["deals_in_progress", "Deals in progress"],
  ["leads_generated", "Leads generated"], ["audience_reach", "Audience reach"],
  ["impressions", "Impressions"], ["engagement", "Engagement (%)"],
];
const blank = { title: "", starts_at: "", location: "", country: "", type: "marketing", description: "", link: "", image_url: PRESETS[0], metrics: Object.fromEntries(NUMS.map(([k]) => [k, ""])) };

export default function MyEvents() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(sp.get("new") === "1");
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(blank);
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(false);
  const [countries, setCountries] = useState([]);
  useEffect(() => {
    api.get("/meta").then((r) => setCountries(Array.isArray(r.data?.countries) ? r.data.countries : [])).catch(() => {});
  }, []);

  const load = () => api.get("/events", { params: { company_id: user.company_id } }).then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startEdit = (e) => {
    setEditId(e.id);
    setF({ ...blank, ...e, metrics: { ...blank.metrics, ...e.metrics } });
    setOpen(true);
  };
  const close = (o) => {
    setOpen(o);
    if (!o) {
      setEditId(null);
      setF(blank);
      if (sp.get("new")) setSp({});
    }
  };
  const save = async () => {
    setBusy(true);
    const metrics = Object.fromEntries(NUMS.map(([k]) => [k, Number(f.metrics[k] || 0)]));
    const body = { title: f.title, starts_at: f.starts_at, location: f.location, country: f.country || "", type: f.type, description: f.description, link: f.link, image_url: f.image_url, metrics };
    try {
      if (editId) await api.put(`/events/${editId}`, body);
      else await api.post("/events", body);
      toast.success(editId ? "Event updated" : "Event published to the public feed");
      close(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/events/${id}`).catch((e) => toast.error(errMsg(e)));
    load();
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <div data-testid="my-events-page">
      <PageHead kicker="Visible to everyone · metrics to members" title="Your events">
        <button className="btn-gold !py-2.5" onClick={() => setOpen(true)} data-testid="post-event-button"><Plus className="h-4 w-4" />Post event</button>
      </PageHead>
      {!items.length && <p className="rounded-2xl border border-dashed border-[#2A1240]/15 p-10 text-center text-slate-300" data-testid="my-events-empty">No events yet. Post your first one.</p>}
      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((e, i) => (
          <div key={e.id} className="relative">
            <EventCard e={e} i={i} onOpen={setView} />
            <div className="absolute top-14 right-4 flex gap-2">
              <button onClick={() => startEdit(e)} className="grid h-8 w-8 place-items-center rounded-full glass hover:border-[#E0218A]" aria-label="Edit" data-testid={`event-edit-${e.id}`}><Pencil className="h-3.5 w-3.5" /></button>
              <button onClick={() => del(e.id)} className="grid h-8 w-8 place-items-center rounded-full glass hover:text-red-300" aria-label="Delete" data-testid={`event-delete-${e.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
      <EventDialog event={view} onClose={() => setView(null)} />
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="bg-[#FFFFFF] border-[#2A1240]/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-lenis-prevent data-testid="event-form-dialog">
          <DialogTitle className="font-display text-2xl font-normal">{editId ? "Edit event" : "Post an event"}</DialogTitle>
          <div className="grid gap-3">
            <input className="field" placeholder="Event title" value={f.title} onChange={set("title")} data-testid="event-title-input" />
            <div className="grid sm:grid-cols-2 gap-3">
              <input className="field" type="datetime-local" value={f.starts_at} onChange={set("starts_at")} data-testid="event-date-input" />
              <select className="field" value={f.type} onChange={set("type")} data-testid="event-type-select">
                <option value="marketing">Marketing</option>
                <option value="partnership">Partnership</option>
              </select>
            </div>
            <input className="field" placeholder="Location (city and country, venue or virtual)" value={f.location} onChange={set("location")} data-testid="event-location-input" />
            <select className="field" value={f.country || ""} onChange={set("country")} data-testid="event-country-select">
              <option value="">Country (optional)</option>
              {countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <textarea className="field" rows={4} placeholder="Description" value={f.description} onChange={set("description")} data-testid="event-description-input" />
            <input className="field" placeholder="Link (RSVP, website)" value={f.link} onChange={set("link")} data-testid="event-link-input" />
            <input className="field" placeholder="Image URL" value={f.image_url} onChange={set("image_url")} data-testid="event-image-input" />
            <div className="flex gap-2">
              {PRESETS.map((p) => (
                <button key={p} type="button" onClick={() => setF({ ...f, image_url: p })} className={`h-14 w-20 rounded-lg overflow-hidden border-2 ${f.image_url === p ? "border-[#E0218A]" : "border-transparent"}`} data-testid={`event-image-preset-${p.split("/").pop().split(".")[0]}`}>
                  <img src={p} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
            <p className="kicker mt-3">Partnership metrics</p>
            <div className="grid grid-cols-2 gap-3">
              {NUMS.map(([k, l]) => (
                <label key={k} className="text-xs text-slate-400">{l}
                  <input className="field mt-1" type="number" min="0" step="any" value={f.metrics[k]} onChange={(e) => setF({ ...f, metrics: { ...f.metrics, [k]: e.target.value } })} data-testid={`event-metric-${k}`} />
                </label>
              ))}
            </div>
            <p className="text-xs text-slate-400">ROI and cost per acquisition are calculated from spend, revenue and deals won.</p>
            <button className="btn-gold mt-2" disabled={busy || !f.title || !f.starts_at} onClick={save} data-testid="event-save-button">{busy ? "Saving…" : editId ? "Save changes" : "Publish event"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
